import { useEffect, useRef, useState } from "react";
import { animate, AnimatePresence, motion, useInView, useMotionValue, useTransform } from "framer-motion";
import { Sparkles, AlertCircle } from "lucide-react";

const KEY = "upt_vagas_v2";
const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
// fase 1: 150 → 7 (ritmo ágil) → 0 · fase 2: +10 vagas → 4 (devagar) → pausa → 1 → 0 (encerrado)
const START = { phase: 1, vagas: 150 };

const load = () => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    return s && typeof s.vagas === "number" ? s : START;
  } catch {
    return START;
  }
};

// usado pelos pop-ups para manter os números coerentes com o contador
export const getVagasInfo = () => {
  const { phase, vagas } = load();
  const sold = phase === 1 ? 150 - vagas : phase === 2 ? 150 + (10 - vagas) : 160;
  return { phase, remaining: phase === 3 ? 0 : vagas, sold };
};

const nextStep = ({ phase, vagas }) => {
  if (phase === 1) {
    if (vagas > 7) return [{ phase, vagas: Math.max(7, vagas - rand(1, 3)) }, rand(1400, 2600)];
    if (vagas === 7) return [{ phase, vagas: 0 }, rand(3500, 5000)];
    return [{ phase: 2, vagas: 10 }, 7000];
  }
  if (phase === 2) {
    if (vagas > 4) return [{ phase, vagas: vagas - 1 }, rand(16000, 28000)];
    if (vagas > 1) return [{ phase, vagas: vagas - 1 }, vagas === 4 ? rand(70000, 100000) : rand(35000, 50000)];
    if (vagas === 1) return [{ phase: 3, vagas: 0 }, rand(30000, 45000)];
  }
  return null;
};

const MESSAGES = {
  extra: {
    Icon: Sparkles,
    title: "Vagas esgotadas — e mais 10 liberadas",
    text: "A procura foi tão grande que a turma lotou. Para não deixar ninguém de fora, o Sacerdote Edvilson decidiu abrir 10 vagas extras.",
  },
  closed: {
    Icon: AlertCircle,
    title: "Todas as vagas foram preenchidas",
    text: "Verifique no link de compra se a oferta ainda está disponível para você — novas vagas podem ser liberadas a qualquer momento.",
  },
};

export const VagasCounter = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [state, setState] = useState(load);
  const mv = useMotionValue(state.vagas);
  const shown = useTransform(mv, (v) => Math.round(v));

  useEffect(() => {
    const c = animate(mv, state.vagas, { duration: 1.1, ease: [0.22, 1, 0.36, 1] });
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
    return () => c.stop();
  }, [state, mv]);

  useEffect(() => {
    if (!inView) return undefined;
    const step = nextStep(state);
    if (!step) return undefined;
    const t = setTimeout(() => setState(step[0]), step[1]);
    return () => clearTimeout(t);
  }, [state, inView]);

  const total = state.phase === 1 ? 150 : 10;
  const pct = (state.vagas / total) * 100;
  const msg = state.phase === 3 ? MESSAGES.closed : state.phase === 2 && state.vagas === 10 ? MESSAGES.extra : state.phase === 1 && state.vagas === 0 ? MESSAGES.extra : null;

  return (
    <div ref={ref} data-testid="vagas-counter" className="rounded-3xl border border-white/10 bg-[#0b1510]/55 p-6 backdrop-blur-md">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-[var(--cream)]">
          <span className="relative flex h-2 w-2">
            {state.phase !== 3 && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/60" />}
            <span className={`relative inline-flex h-2 w-2 rounded-full ${state.phase === 3 ? "bg-[#c2574e]" : "bg-emerald-400"}`} />
          </span>
          {state.phase === 1 ? "Vagas da turma" : state.phase === 2 ? "Vagas extras" : "Turma encerrada"}
        </span>
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#f5f1e6]/60">{total} {state.phase === 1 ? "iniciais" : "extras"}</span>
      </div>
      <div className="mt-4 flex items-end gap-3">
        <motion.span data-testid="vagas-count" className="font-display text-6xl leading-none text-[var(--cream)] md:text-7xl">
          {shown}
        </motion.span>
        <span className="mb-2 text-sm text-[#f5f1e6]/75">vagas restantes</span>
      </div>
      <div className="mt-5 h-[3px] w-full overflow-hidden rounded-full bg-white/10">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-[#d9b45a] to-[#f0d58f]"
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.2em] text-[#f5f1e6]/55">
        <span>{total - state.vagas} preenchidas</span>
        <span>{Math.round(pct)}% disponível</span>
      </div>
      <AnimatePresence>
        {msg && (
          <motion.div
            key={msg.title}
            data-testid="vagas-message"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 20 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="flex gap-3 rounded-2xl border border-[#d9b45a]/30 bg-[#d9b45a]/[0.08] p-4">
              <msg.Icon size={18} className="mt-0.5 shrink-0 text-[var(--gold)]" />
              <div>
                <p className="text-sm font-semibold text-[var(--gold)]">{msg.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-[#f5f1e6]/85">{msg.text}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
